import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, getAuthToken } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Palette, Upload, Image as ImageIcon, Save, Trash2 } from "lucide-react";

const PRESET_COLORS = [
  { name: "Slate (default)", color: "#0F172A" },
  { name: "Sky",             color: "#0EA5E9" },
  { name: "Indigo",          color: "#4F46E5" },
  { name: "Emerald",         color: "#10B981" },
  { name: "Amber",           color: "#F59E0B" },
  { name: "Rose",            color: "#E11D48" },
  { name: "Violet",          color: "#8B5CF6" },
  { name: "Cyprus Blue",     color: "#003087" },
];

/**
 * Whitelabel customisation card — platform title, theme color, logo upload.
 * Logo constraints (per spec Q2-a): PNG/JPG/SVG/WEBP, ≤1 MB, ≤1024×1024 px.
 * Theme color applies to TopNav background + primary buttons + brand accents
 * via the `--brand-color` CSS variable set on <html> in TopNav.
 */
export default function WhitelabelSettings() {
  const fileRef = useRef(null);
  const [s, setS] = useState(null);
  const [title, setTitle] = useState("");
  const [color, setColor] = useState("#0F172A");
  const [textColor, setTextColor] = useState("#FFFFFF");
  const [busy, setBusy] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);

  const load = () => {
    if (!getAuthToken()) {
      // Legacy MASTER_JOHN: no JWT; just lock the card to read-only defaults.
      setS({ tenant_id: "MASTER_JOHN", platform_title: "KioskForce ERP", theme_color: "#0F172A", theme_text_color: "#FFFFFF", legacy: true });
      return;
    }
    api.get("/tenant-settings").then(({ data }) => {
      setS(data);
      setTitle(data.platform_title || "");
      setColor(data.theme_color || "#0F172A");
      setTextColor(data.theme_text_color || "#FFFFFF");
    }).catch(() => setS(null));
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch("/tenant-settings", {
        platform_title: title.trim() || null,
        theme_color: color,
        theme_text_color: textColor,
      });
      setS(data);
      toast.success("Whitelabel saved — refresh the kiosk to see the new theme everywhere");
      // Apply immediately to the CSS variable too.
      document.documentElement.style.setProperty("--brand-color", data.theme_color);
      document.documentElement.style.setProperty("--brand-text", data.theme_text_color);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    if (file.size > 1024 * 1024) {
      toast.error("Logo must be ≤ 1 MB");
      return;
    }
    const ok = ["image/png", "image/jpeg", "image/jpg", "image/svg+xml", "image/webp"].includes(file.type);
    if (!ok) {
      toast.error(`Unsupported type ${file.type}. Use PNG / JPG / SVG / WEBP.`);
      return;
    }
    setUploadBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/tenant-settings/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setS(data);
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploadBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeLogo = async () => {
    if (!window.confirm("Remove the uploaded logo?")) return;
    try {
      const { data } = await api.delete("/tenant-settings/logo");
      setS(data);
      toast.success("Logo removed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Remove failed");
    }
  };

  if (!s) return null;
  const isLegacy = s.legacy;

  return (
    <Card className="p-5" data-testid="whitelabel-settings">
      <div className="flex items-center gap-2 mb-4">
        <div className="h-10 w-10 rounded-lg bg-violet-50 text-violet-700 flex items-center justify-center">
          <Palette className="h-5 w-5" />
        </div>
        <div>
          <h3 className="font-bold text-lg leading-tight">Whitelabel · Branding</h3>
          <p className="text-[11px] text-slate-500 tracking-wide">
            Λογότυπο · Τίτλος · Χρώμα — visible across the kiosk app
          </p>
        </div>
      </div>

      {isLegacy && (
        <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-3">
          Legacy MASTER_JOHN session — whitelabel editing requires the new JWT tenant login.
        </p>
      )}

      {/* Logo */}
      <div className="mb-5">
        <Label className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1 mb-2">
          <ImageIcon className="h-3 w-3" /> Logo · Λογότυπο
        </Label>
        <div className="flex items-center gap-4">
          <div
            className="h-16 w-16 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden"
            data-testid="whitelabel-logo-preview"
          >
            {s.logo_url ? (
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}${s.logo_url}&v=${s.logo_file_id}`}
                alt="logo"
                className="h-16 w-16 object-contain"
              />
            ) : (
              <ImageIcon className="h-7 w-7 text-slate-300" />
            )}
          </div>
          <div className="flex flex-col gap-2 flex-1">
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/svg+xml,image/webp"
              onChange={(e) => uploadLogo(e.target.files?.[0])}
              className="hidden"
              data-testid="whitelabel-logo-input"
            />
            <div className="flex gap-2 flex-wrap">
              <Button
                size="sm"
                variant="outline"
                onClick={() => fileRef.current?.click()}
                disabled={uploadBusy || isLegacy}
                className="gap-1 h-8"
                data-testid="whitelabel-upload-btn"
              >
                <Upload className="h-3.5 w-3.5" />
                <span className="text-[11px]">{uploadBusy ? "Uploading…" : "Upload Logo"}</span>
              </Button>
              {s.logo_url && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={removeLogo}
                  disabled={isLegacy}
                  className="gap-1 h-8 border-red-300 text-red-700 hover:bg-red-50"
                  data-testid="whitelabel-remove-btn"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  <span className="text-[11px]">Remove</span>
                </Button>
              )}
            </div>
            <p className="text-[10px] text-slate-500">
              PNG · JPG · SVG · WEBP · max 1 MB · max 1024×1024 px
            </p>
          </div>
        </div>
      </div>

      {/* Platform title */}
      <div className="mb-5">
        <Label className="text-[10px] uppercase tracking-wider text-slate-500">
          Platform Title · Τίτλος Πλατφόρμας
        </Label>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Acme Operations"
          disabled={isLegacy}
          data-testid="whitelabel-title"
          className="mt-1"
        />
        <p className="text-[10px] text-slate-500 mt-1">
          Replaces &quot;KioskForce ERP&quot; in the TopNav for this tenant only.
        </p>
      </div>

      {/* Theme color */}
      <div className="mb-5">
        <Label className="text-[10px] uppercase tracking-wider text-slate-500">
          Theme Color · Χρώμα Θέματος
        </Label>
        <div className="flex items-center gap-2 mt-2">
          <input
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            disabled={isLegacy}
            className="h-10 w-14 rounded border border-slate-200 bg-white cursor-pointer disabled:cursor-not-allowed"
            data-testid="whitelabel-color"
          />
          <Input
            value={color}
            onChange={(e) => setColor(e.target.value)}
            disabled={isLegacy}
            className="font-mono w-32"
          />
          <div className="flex gap-1 ml-2 flex-wrap">
            {PRESET_COLORS.map((p) => (
              <button
                key={p.color}
                onClick={() => setColor(p.color)}
                disabled={isLegacy}
                style={{ backgroundColor: p.color }}
                title={`${p.name} · ${p.color}`}
                className={`h-7 w-7 rounded-full border-2 transition-transform hover:scale-110 ${
                  color.toLowerCase() === p.color.toLowerCase() ? "border-slate-900" : "border-white shadow-sm"
                }`}
                data-testid={`whitelabel-preset-${p.color.slice(1).toLowerCase()}`}
              />
            ))}
          </div>
        </div>
        <p className="text-[10px] text-slate-500 mt-2">
          Applies to TopNav background + brand accents only. Text on cards remains slate for readability.
        </p>
      </div>

      <Button
        onClick={save}
        disabled={busy || isLegacy}
        className="w-full h-10 gap-2 font-bold"
        style={{ backgroundColor: color, color: textColor }}
        data-testid="whitelabel-save"
      >
        <Save className="h-4 w-4" />
        {busy ? "Saving…" : "Save Whitelabel"}
      </Button>
    </Card>
  );
}
